import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'

import WeatherApi from '@/api/config/weatherApi'

import { SRCInterface } from '@/store/types/srcInterfaceType'
import {
  getColumnDetails,
  WeatherReportFields,
} from '@store/slices/WeatherDashboardV2/types'
import {
  ChartData,
  DataComparisonState,
  SensorDataColumns,
  SensorHistoricDataRequestParams,
  Sensors,
  SensorTypes,
  TimeGrouping,
} from '../types/DataComparisonType'

import { staticConvertToMetric } from '@/hooks/useUnits'
import { AssetData } from './WeatherDashboardV2/types'
import { formatNumber } from '@/ultils/number'

export const seedWeatherStationDataColumns: SensorDataColumns[] =
  Object.values(WeatherReportFields)

export const seedSensorData: Sensors[] = [
  {
    id: 8,
    typeId: 1,
    name: 'West Torrens',
    dataColumns: seedWeatherStationDataColumns,
  },
  {
    id: 2,
    typeId: 1,
    name: 'SB3',
    dataColumns: seedWeatherStationDataColumns,
  },
]

export const seedSensorTypes: SensorTypes[] = [
  {
    id: 1,
    name: 'Weather Station',
  },
]

const initialState: DataComparisonState = {
  sensors: {
    isLoading: false,
    data: [],
  },
  sensorTypes: {
    isLoading: false,
    data: [],
  },
  chartDetails: {
    isLoading: false,
    data: [],
  },
  currentCSVData: undefined,
  rightSidebarDetails: {
    variantName: '',
    sensorStateData: [],
    allSensorStateData: [],
    checkedSensorParameter: [],
    openSensor: [],
    sensorType: [],
    inputValue: '',
  },
  timeGrouping: '',
}

export const sensorTypeOrder = [
  'Weather Station',
  'Barometer Sensor',
  'PAR Sensor',
  'Indoor Climate Sensor',
  'Soil Moisture Sensor',
  'pH Sensor',
]

export const fetchSensorHistoricData = createAsyncThunk(
  'weatherDashboard/fetchSensorHistoricData',
  async (params: SensorHistoricDataRequestParams, { rejectWithValue }) => {
    try {
      const allSensorHistoricData: ChartData[] = []

      for (const sensorRequest of params.sensorList) {
        const { data } = await WeatherApi.getWeatherReportV2({
          AssetId: sensorRequest.sensorId,
          TimeBucket: 1,
          FromTime: params.startDate,
          ToTime: params.endDate,
          Names: sensorRequest.dataColumns,
          PageSize: 10000000,
          PageIndex: 1,
        })

        allSensorHistoricData.push(
          ...convertToChartData(
            data.data,
            sensorRequest.sensorId,
            sensorRequest.sensorName,
            sensorRequest.metadata,
          ),
        )
      }
      return allSensorHistoricData
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

function convertToChartData(
  rawData: any[],
  sensorId: number,
  sensorName: string,
  metadata: any,
): ChartData[] {
  const nullableKeys = [
    WeatherReportFields.Evapotranspiration.columnName.toLowerCase(),
    WeatherReportFields.RainFall.columnName.toLowerCase(),
  ]
  const chartMap: Record<string, ChartData> = {}
  rawData.forEach((item) => {
    const timestamp = item.time as string

    Object.entries(item).forEach(([key, value]) => {
      if (key === 'time') return

      const columnDetail = getColumnDetails(key)
      if (!chartMap[key]) {
        chartMap[key] = {
          sensorId,
          sensorName,
          chartAxisName: columnDetail?.chartAxisName ?? '',
          columnName: columnDetail?.displayName ?? '',
          unitMetric: columnDetail?.metricUnit
            ? columnDetail?.metricUnit
            : metadata?.metric ?? '',
          unitImperial: columnDetail?.imperialUnit
            ? columnDetail?.imperialUnit
            : metadata?.imperial ?? '',
          chartType: columnDetail?.chartType
            ? columnDetail?.chartType
            : metadata?.chartType ?? '',
          isFixed: columnDetail?.isFixed ?? 2,
          data: [],
        }
      }

      if (value || value === 0 || nullableKeys.includes(key)) {
        const valueNumber = typeof value === 'number' ? value : Number(value)

        const metricValue = Number(Math.round(valueNumber * 100) / 100)
        chartMap[key].data.push({
          timestamp: timestamp,
          value: metricValue,
        })
      }
    })
  })

  return Object.values(chartMap)
}

export const fetchSensorData = createAsyncThunk(
  'dataComparison/fetchSensorData',
  async (interfaceId: string, { rejectWithValue }) => {
    try {
      return seedSensorData as Sensors[]
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export const fetchSensorTypes = createAsyncThunk(
  'dataComparison/fetchSensorTypes',
  async (interfaceId: string, { rejectWithValue }) => {
    try {
      return seedSensorTypes as SensorTypes[]
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export const dataComparisonSliceV2 = createSlice({
  name: 'theme',
  initialState,
  reducers: {
    setupWeatherStationDataV2: (
      state,
      action: PayloadAction<{
        assetData: AssetData[]
        sensorTypes: SensorTypes[]
      }>,
    ) => {
      const { assetData, sensorTypes } = action.payload
      const extractedSensorData = assetData
        .map((item) => {
          const typeName = item.metadata?.type || 'Other'
          const matchedType = sensorTypes?.find((st) => st.name === typeName)
          if (!matchedType?.id) return null

          return {
            id: item.id,
            typeId: matchedType.id,
            name: item.name,
            dataColumns: seedWeatherStationDataColumns,
            metadata: item.metadata,
          }
        })
        .filter((item): item is NonNullable<typeof item> => item !== null)
      state.sensors.data = extractedSensorData
    },
    setRightSidePanelV2: (state, action) => {
      const sensors = [...action.payload.sensorType]

      sensors.sort(
        (a, b) =>
          sensorTypeOrder.indexOf(a.name) - sensorTypeOrder.indexOf(b.name),
      )
      state.rightSidebarDetails = action.payload
      state.rightSidebarDetails.sensorType = sensors
    },
    setCurrentCSVDataV2: (state, action) => {
      state.currentCSVData = action.payload
    },
    setTimeGrouping: (
      state,
      action: PayloadAction<{
        timeGrouping: TimeGrouping | ''
      }>,
    ) => {
      state.timeGrouping = action.payload.timeGrouping
    },
    setChartLoadingV2: (state, action: PayloadAction<boolean>) => {
      state.chartDetails.isLoading = action.payload
      state.chartDetails.data = []
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchSensorData
      .addCase(fetchSensorData.pending, (state) => {
        state.sensors.isLoading = true
      })
      .addCase(fetchSensorData.fulfilled, (state, action) => {
        state.sensors.isLoading = false
        state.sensors.data = action.payload
      })
      .addCase(fetchSensorData.rejected, (state) => {
        state.sensors.isLoading = false
      })
      // fetchSensorTypes
      .addCase(fetchSensorTypes.pending, (state) => {
        state.sensorTypes.isLoading = true
      })
      .addCase(fetchSensorTypes.fulfilled, (state, action) => {
        state.sensors.isLoading = false
        state.sensorTypes.data = action.payload
      })
      .addCase(fetchSensorTypes.rejected, (state) => {
        state.sensors.isLoading = false
      })
      // fetchSensorHistoricData
      .addCase(fetchSensorHistoricData.pending, (state) => {
        state.chartDetails.isLoading = true
      })
      .addCase(fetchSensorHistoricData.fulfilled, (state, action) => {
        state.chartDetails.isLoading = false
        state.chartDetails.data = action.payload
      })
      .addCase(fetchSensorHistoricData.rejected, (state) => {
        state.chartDetails.isLoading = false
      })
  },
})
// Action creators are generated for each case reducer function
export const {
  setupWeatherStationDataV2,
  setRightSidePanelV2,
  setCurrentCSVDataV2,
  setTimeGrouping,
  setChartLoadingV2,
} = dataComparisonSliceV2.actions

export default dataComparisonSliceV2.reducer
